// Settings card (admin only, Session ARZ, Item 1): how a subcontractor's Xero payable contact is set up when the office
// sends it to Xero from the subcontractor's page. Two settings, both optional and never guessed:
//   - the expense account that bills from subcontractors default to (Xero: the contact's default purchases account);
//   - the bill payment terms (Xero: the contact's payment terms for bills, a day with how it is counted).
// A blank setting is left out of what is sent, so Xero keeps whatever it already has. Nothing here talks to Xero.
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import {
  BILL_PAYMENT_TERM_LABELS,
  XERO_BILL_PAYMENT_TERM_TYPES,
  isDayOfMonthTermType,
  type SubcontractorXeroSettings,
  type XeroBillPaymentTermTypeKey,
} from "../../lib/subcontractorPayments";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

interface Form {
  code: string;
  days: string;
  type: XeroBillPaymentTermTypeKey | "";
}

function toForm(s: SubcontractorXeroSettings): Form {
  return {
    code: s.purchasesAccountCode ?? "",
    days: s.billPaymentDays == null ? "" : String(s.billPaymentDays),
    type: s.billPaymentTermType ?? "",
  };
}

// Client-side check before the round trip; the server (and a database CHECK) is the authority.
function formError(f: Form): string | null {
  const code = f.code.trim();
  if (code && !/^[A-Za-z0-9]{1,10}$/.test(code)) return "Enter a Xero account code: letters and digits only, up to 10 characters.";
  const hasDays = f.days.trim() !== "";
  if (hasDays !== (f.type !== "")) return "Set both the payment day and how it is counted, or clear both.";
  if (!hasDays || f.type === "") return null;
  const n = Number(f.days);
  if (!Number.isInteger(n)) return "Enter the payment day as a whole number.";
  if (isDayOfMonthTermType(f.type)) return n >= 1 && n <= 31 ? null : "A day of the month must be from 1 to 31.";
  return n >= 0 && n <= 365 ? null : "The number of days must be from 0 to 365.";
}

export function SubcontractorXeroSettingsCard({ refreshKey }: { refreshKey?: number }) {
  const [settings, setSettings] = useState<SubcontractorXeroSettings | null>(null);
  const [form, setForm] = useState<Form>({ code: "", days: "", type: "" });
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setState("loading");
    api.settings
      .subcontractorXero()
      .then((s) => {
        if (!live) return;
        setSettings(s);
        setForm(toForm(s));
        setState("ready");
      })
      .catch(() => live && setState("error"));
    return () => {
      live = false;
    };
  }, [refreshKey]);

  async function save() {
    const problem = formError(form);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = await api.settings.updateSubcontractorXero({
        purchasesAccountCode: form.code.trim() || null,
        billPaymentDays: form.days.trim() === "" ? null : Number(form.days),
        billPaymentTermType: form.type === "" ? null : form.type,
      });
      setSettings(next);
      setForm(toForm(next));
      setEditing(false);
    } catch (e) {
      setError(friendlyApiError(e, "Could not save the subcontractor Xero settings. Please try again in a moment."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="mt-5 rounded-ele border border-ele-purple-accent bg-ele-surface p-5" aria-label="Subcontractor bills in Xero">
      <h2 className="mb-2 font-heading text-lg text-ele-blue">Subcontractor bills in Xero</h2>
      <p className="mb-3 text-sm text-ele-body">
        Used when you send a subcontractor to Xero from their page, so their bills are set up the same way every time. A
        setting left blank is not sent, and Xero keeps whatever it already has. The account code comes from your Xero
        chart of accounts (an expense or direct costs account).
      </p>
      {state === "loading" ? (
        <Loader shape="inline" />
      ) : state === "error" || !settings ? (
        <p role="alert" className="text-sm text-ele-strong">Could not load the subcontractor Xero settings.</p>
      ) : editing ? (
        <div className="space-y-3">
          <label className="block">
            <span className={labelCls}>Expense account code</span>
            <input
              aria-label="Expense account code"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
              placeholder="For example 310"
              className={`${inputCls} w-40`}
            />
          </label>
          <div className="flex flex-wrap gap-3">
            <label className="block">
              <span className={labelCls}>Bills are due</span>
              <input
                aria-label="Bill payment day"
                type="number"
                inputMode="numeric"
                value={form.days}
                onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
                className={`${inputCls} w-24`}
              />
            </label>
            <label className="block">
              <span className={labelCls}>Counted as</span>
              <select
                aria-label="How the bill payment day is counted"
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as Form["type"] }))}
                className={inputCls}
              >
                <option value="">Not set</option>
                {XERO_BILL_PAYMENT_TERM_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {BILL_PAYMENT_TERM_LABELS[t]}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {error && <p role="alert" className="text-sm text-ele-danger">{error}</p>}
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setForm(toForm(settings));
                setError(null);
                setEditing(false);
              }}
              disabled={saving}
              className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ele-body">Expense account code</dt>
              <dd className="text-ele-strong">{settings.purchasesAccountCode ?? "Not set"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ele-body">Bills are due</dt>
              <dd className="text-right text-ele-strong">{settings.billPaymentTermsText ?? "Not set"}</dd>
            </div>
          </dl>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="mt-3 rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
          >
            Edit
          </button>
        </>
      )}
    </section>
  );
}
